import { Autocomplete, Card, CircularProgress, FormControlLabel, Grid, Icon, Radio, RadioGroup, TextField, ThemeProvider, createTheme } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import postRevenueExpense, { deleteRevenueExpense, getRevenueExpenseAutocomplete, getRevenueExpenseById, getRevenueExpenseCategoryLimits, updateRevenueExpense } from './revenueExpensesApi';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ptBR } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/pt-br';
import dayjs from "dayjs";
import { Steps } from "intro.js-react";
import { useMaterialUIController } from "context";
import { completeOnboarding, isOnboardingCompleted } from "utils/onboarding";
import { formatRevenueExpenseCategory, getRevenueExpenseCategoryOption, getRevenueExpenseCategoryOptions, normalizeRevenueExpenseCategory } from "./revenueExpenseCategories";
import RevenueExpenseCategoryLimitModal from "./RevenueExpenseCategoryLimitModal";
import { useDebounce } from "use-lodash-debounce";

const REVENUE_EXPENSES_MODAL_ONBOARDING_KEY = "patrimonio360onboarding-modal-revenuexexpenses";
const EXPENSE_TYPE = 2;

function AddNewRevenueExpenseForm({ title, id, openSnackBar, closeModal, refreshList, refreshSummaryBoxes }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [operationType, setOperationType] = useState('');
    const [date, setDate] = useState(dayjs());
    const [value, setValue] = useState('');
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');
    const [isLoading, setIsLoagding] = useState(false);
    const [isLoadingNameSuggestions, setIsLoadingNameSuggestions] = useState(false);
    const [nameSuggestions, setNameSuggestions] = useState([]);
    const [nameSuggestionsError, setNameSuggestionsError] = useState(false);
    const [stepsEnabled, setStepsEnabled] = useState(false);
    const [categoryLimits, setCategoryLimits] = useState([]);
    const [shouldContinueSaveAfterCategoryLimit, setShouldContinueSaveAfterCategoryLimit] = useState(false);
    const [categoryLimitModalState, setCategoryLimitModalState] = useState({
        open: false,
        category: '',
        maxValue: '',
    });
    const autocompleteAppliedRef = useRef(false);
    const categoryOptions = useMemo(() => getRevenueExpenseCategoryOptions(operationType), [operationType]);
    const selectedCategoryOption = useMemo(
        () => getRevenueExpenseCategoryOption(category),
        [category]
    );
    const debouncedName = useDebounce(name, 300);

    useEffect(() => {
        if (id) {
            getRevenueExpense();
        }

        loadCategoryLimits();
        setStepsEnabled(!isOnboardingCompleted(REVENUE_EXPENSES_MODAL_ONBOARDING_KEY));
    }, []);

    useEffect(() => {
        if (id) {
            return undefined;
        }

        const search = debouncedName.trim();
        if (search.length < 2) {
            setNameSuggestions([]);
            setNameSuggestionsError(false);
            setIsLoadingNameSuggestions(false);

            if (autocompleteAppliedRef.current) {
                setCategory('');
                setOperationType('');
                autocompleteAppliedRef.current = false;
            }

            return undefined;
        }

        let isCurrentRequest = true;
        setIsLoadingNameSuggestions(true);
        setNameSuggestionsError(false);

        getRevenueExpenseAutocomplete(search)
            .then((data) => {
                if (!isCurrentRequest) {
                    return;
                }

                const suggestions = Array.isArray(data) ? data : [];
                setNameSuggestions(suggestions);

                const exactMatch = suggestions.find(
                    (suggestion) => suggestion.name?.trim().toLocaleLowerCase() === search.toLocaleLowerCase()
                );

                const bestMatch = exactMatch ?? suggestions[0];

                if (bestMatch) {
                    setCategory(bestMatch.category ?? '');
                    setOperationType(bestMatch.type === 1 ? 'receita' : 'despesa');
                    autocompleteAppliedRef.current = true;
                } else if (autocompleteAppliedRef.current) {
                    setCategory('');
                    setOperationType('');
                    autocompleteAppliedRef.current = false;
                }

                if (exactMatch) {
                    setName(exactMatch.name ?? '');
                }
            })
            .catch(() => {
                if (isCurrentRequest) {
                    setNameSuggestions([]);
                    setNameSuggestionsError(true);
                }
            })
            .finally(() => {
                if (isCurrentRequest) {
                    setIsLoadingNameSuggestions(false);
                }
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [debouncedName, id]);

    const loadCategoryLimits = async () => {
        await getRevenueExpenseCategoryLimits(EXPENSE_TYPE)
            .then((data) => {
                setCategoryLimits(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                setCategoryLimits([]);
            });
    };

    const getRevenueExpense = async () => {
        await getRevenueExpenseById(id)
            .then(e => {
                setDate(e.makeDate);
                setValue(e.value);
                setName(e.name);
                setCategory(e.category ?? '');
                setOperationType(e.type == 1 ? 'receita' : 'despesa');
            });
    }

    const theme = createTheme(
        {
            palette: {
                primary: { main: '#1976d2' },
            },
        },
        ptBR,
    );

    const handleDateChange = (date) => {
        console.log(dayjs());
        const currentTime = dayjs().format('HH:mm:ss');

        const fullDateTime = dayjs(date).format(`YYYY-MM-DD`) + 'T' + currentTime;
        setDate(fullDateTime);
    };

    const persistRevenueExpense = async () => {
        const body = {
            type: operationType === 'receita' ? 1 : 2,
            date: date,
            description: '',
            value: value,
            name: name,
            category: category?.trim() || null
        };

        if (id) {
            await updateRevenueExpense(id, body)
                .then((data) => {
                    setIsLoagding(false);
                    openSnackBar({ type: 'success', message: 'Operação realizada com sucesso' });
                    closeModal();
                    refreshList();
                    refreshSummaryBoxes();
                })
                .catch(e => {
                    setIsLoagding(false);
                    openSnackBar({ type: 'alert', message: `${e.message}` });
                });
        } else {
            await postRevenueExpense(body)
                .then((data) => {
                    setIsLoagding(false);
                    if (data?.errorCode) {
                        openSnackBar({ type: 'error', message: `${data?.message}`, isUpgradeMessage: data?.errorCode == 402 });
                        return;
                    }

                    openSnackBar({ type: 'success', message: 'Operação realizada com sucesso' });
                    // closeModal();
                    resetForm({
                        categoryToKeep: body.category ?? '',
                        dateToKeep: body.date,
                    });
                    refreshList();
                    // refreshSummaryBoxes();
                })
                .catch(e => {
                    setIsLoagding(false);
                    openSnackBar({ type: 'error', message: `${e}` });
                });
        }
    };

    const hasCategoryLimit = (categoryValue) =>
        categoryLimits.some(
            (item) => normalizeRevenueExpenseCategory(item.category) === normalizeRevenueExpenseCategory(categoryValue)
        );

    const openCategoryLimitModal = (categoryValue) => {
        const existingLimit = categoryLimits.find(
            (item) => normalizeRevenueExpenseCategory(item.category) === normalizeRevenueExpenseCategory(categoryValue)
        );

        setCategoryLimitModalState({
            open: true,
            category: categoryValue.trim(),
            maxValue: existingLimit?.maxValue ?? '',
        });
    };

    const handleSave = async () => {
        const normalizedCategory = category?.trim();

        if (operationType === 'despesa' && normalizedCategory && !hasCategoryLimit(normalizedCategory)) {
            setShouldContinueSaveAfterCategoryLimit(true);
            openCategoryLimitModal(normalizedCategory);
            return;
        }

        setIsLoagding(true);
        await persistRevenueExpense();
    };

    const handleDelete = async () => {
        setIsLoagding(true);
        await deleteRevenueExpense(id)
            .then((data) => {
                setIsLoagding(true);
                openSnackBar({ type: 'success', message: 'Operação realizada com sucesso' });
                closeModal();
                refreshList();
                refreshSummaryBoxes();
            });
    };

    const resetForm = ({ categoryToKeep = category, dateToKeep = date } = {}) => {
        setName('');
        setValue('');
        setCategory(categoryToKeep);
        setDate(dateToKeep);
        setOperationType('');
        autocompleteAppliedRef.current = false;
    };

    const stepsDesktop = [
        {
            element: '#input-ativo',
            intro: 'Aqui você informa o nome da receita ou despesa.',
        },
        {
            element: '.formNewRevenueXExmpenseValue',
            intro: 'Informe o valor aqui.',
        },
        {
            element: '.formNewRevenueXExmpenseDate',
            intro: 'Selecione a data da receita ou despesa.',
        },
        {
            element: '.formNewRevenueXExmpenseRadio',
            intro: 'Selecione se é receita ou despesa e, depois, é só salvar.',
        },
    ];

    const handleCloseOnboarding = () => {
        completeOnboarding(REVENUE_EXPENSES_MODAL_ONBOARDING_KEY);
        setStepsEnabled(false);
    };

    const handleCategoryLimitSaved = (savedItem) => {
        setCategoryLimits((previousState) => {
            const hasExistingItem = previousState.some(
                (item) => normalizeRevenueExpenseCategory(item.category) === normalizeRevenueExpenseCategory(savedItem.category)
            );

            if (hasExistingItem) {
                return previousState.map((item) =>
                    normalizeRevenueExpenseCategory(item.category) === normalizeRevenueExpenseCategory(savedItem.category)
                        ? savedItem
                        : item
                );
            }

            return [...previousState, savedItem];
        });
    };

    const handleCategoryLimitModalClose = () => {
        setCategoryLimitModalState({
            open: false,
            category: '',
            maxValue: '',
        });
        setShouldContinueSaveAfterCategoryLimit(false);
    };

    const handleCategoryAutocompleteChange = (newValue) => {
        const nextCategory = typeof newValue === "string" ? newValue : newValue?.value ?? "";
        setCategory(nextCategory);
        autocompleteAppliedRef.current = false;

        if (operationType === 'despesa' && nextCategory?.trim() && !hasCategoryLimit(nextCategory)) {
            openCategoryLimitModal(nextCategory);
        }
    };

    const handleOperationTypeChange = (nextOperationType) => {
        setOperationType(nextOperationType);
        autocompleteAppliedRef.current = false;

        if (nextOperationType === 'despesa' && category?.trim() && !hasCategoryLimit(category)) {
            openCategoryLimitModal(category);
        }
    };

    const applyNameSuggestion = (suggestion) => {
        if (!suggestion || typeof suggestion === "string") {
            return;
        }

        setName(suggestion.name ?? '');
        setCategory(suggestion.category ?? '');
        setOperationType(suggestion.type === 1 ? 'receita' : 'despesa');
        autocompleteAppliedRef.current = true;
    };

    return (
        <StyledContainer>
            <Steps
                enabled={stepsEnabled}
                steps={stepsDesktop}
                initialStep={0}
                onExit={handleCloseOnboarding}
                // exitOnEsc
                // onBeforeExit={false}
                exitOnOverlayClick={false}
                onComplete={handleCloseOnboarding}
                options={{
                    doneLabel: 'Entendi!',
                    prevLabel: '<',
                    nextLabel: '>'
                }}
            />
            {/* <Grid item xs={12} sm={6} lg={3}>
                <MDSnackbar
                    color={openSnackMessage?.type}
                    icon="check"
                    title={openSnackMessage?.message}
                    open={openSnackMessage}
                    onClose={() => openSnackBar(false)}
                    close={() => openSnackBar(false)}
                />
            </Grid> */}
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <StyledMDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor={operationType == 'receita' ? "success" : "error"}
                                borderRadius="lg"
                                coloredShadow="info"
                            >
                                <MDTypography variant="h6" color="dark">
                                    {title}
                                </MDTypography>
                            </StyledMDBox>
                            <MDBox pt={4} pb={3} px={3}>
                                <MDBox component="form" role="form">
                                    <MDBox mb={2}>
                                        <MDInput type="number" label="Valor" fullWidth className="formNewRevenueXExmpenseValue"
                                            value={value}
                                            onChange={(e) => setValue(e.target.value)} />
                                    </MDBox>
                                    <StyledInput mb={2} id="input-ativo">
                                        <Autocomplete
                                            freeSolo
                                            autoHighlight
                                            openOnFocus
                                            filterOptions={(options) => options}
                                            options={nameSuggestions}
                                            value={name}
                                            inputValue={name}
                                            loading={isLoadingNameSuggestions}
                                            loadingText="Buscando no seu histórico..."
                                            isOptionEqualToValue={(option, selectedValue) =>
                                                typeof selectedValue === "string"
                                                    ? option.name === selectedValue
                                                    : option.name === selectedValue?.name
                                            }
                                            getOptionLabel={(option) =>
                                                typeof option === "string" ? option : option.name ?? ''
                                            }
                                            onChange={(_, newValue) => {
                                                if (typeof newValue === "string") {
                                                    setName(newValue);
                                                    return;
                                                }

                                                applyNameSuggestion(newValue);
                                            }}
                                            onInputChange={(_, newInputValue, reason) => {
                                                if (reason === "input" || reason === "clear") {
                                                    setName(newInputValue);
                                                }
                                            }}
                                            noOptionsText="Nenhum lançamento encontrado"
                                            renderOption={(props, option) => (
                                                <li {...props} key={`${option.name}-${option.type}-${option.category ?? ''}`}>
                                                    <NameSuggestionContent>
                                                        <span>{option.name}</span>
                                                        <small>
                                                            {option.category
                                                                ? `${formatRevenueExpenseCategory(option.category)} • `
                                                                : ''}
                                                            {option.type === 1 ? 'Receita' : 'Despesa'}
                                                        </small>
                                                    </NameSuggestionContent>
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Nome"
                                                    className="formNewRevenueXExmpenseName"
                                                    error={nameSuggestionsError}
                                                    helperText={nameSuggestionsError
                                                        ? "Não foi possível carregar as sugestões. Você ainda pode preencher manualmente."
                                                        : name.trim().length < 2
                                                            ? "Digite pelo menos 2 caracteres para buscar no histórico."
                                                            : "Selecione uma sugestão para preencher categoria e tipo."}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isLoadingNameSuggestions
                                                                    ? <CircularProgress color="inherit" size={16} />
                                                                    : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </StyledInput>
                                    <StyledInput mb={2}>
                                        <Autocomplete
                                            freeSolo
                                            data-input
                                            options={categoryOptions}
                                            value={selectedCategoryOption ?? (category || null)}
                                            isOptionEqualToValue={(option, value) => {
                                                if (typeof value === "string") {
                                                    return option.value === value;
                                                }

                                                return option.value === value?.value;
                                            }}
                                            getOptionLabel={(option) => {
                                                if (typeof option === "string") {
                                                    return option;
                                                }

                                                return formatRevenueExpenseCategory(option.value);
                                            }}
                                            onChange={(_, newValue) => {
                                                handleCategoryAutocompleteChange(newValue);
                                            }}
                                            onInputChange={(_, newInputValue, reason) => {
                                                if (reason === "input" || reason === "clear") {
                                                    setCategory(newInputValue);
                                                }
                                            }}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.value}>
                                                    {formatRevenueExpenseCategory(option.value)}
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Categoria"
                                                    placeholder="Selecione ou digite uma categoria"
                                                />
                                            )}
                                        />
                                    </StyledInput>

                                    <MDBox mb={2}>
                                        <ThemeProvider theme={theme}>
                                            <LocalizationProvider
                                                adapterLocale="pt-br"
                                                dateAdapter={AdapterDayjs}
                                            >
                                                <StyledDatePicker
                                                    $isDarkTheme={darkMode}
                                                    className="formNewRevenueXExmpenseDate"
                                                    sx={{ display: 'flex', justifyContent: 'flex-end' }}
                                                    // format="DD/MM/YYYY"
                                                    label="Data"
                                                    onChange={handleDateChange}
                                                    value={date ? dayjs(date) : null}
                                                    PopperProps={{
                                                        modifiers: [
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, 10], // Deslocamento para ajustar a posição
                                                                }
                                                            }
                                                        ]
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </ThemeProvider>
                                    </MDBox>

                                    <MDBox mb={2}>
                                        <RadioGroup
                                            className="formNewRevenueXExmpenseRadio"
                                            aria-labelledby="demo-radio-buttons-group-label"
                                            defaultValue="receita"
                                            name="radio-buttons-group"
                                            row
                                            value={operationType}
                                            onChange={(e) => handleOperationTypeChange(e.target.value)}
                                        >
                                            <FormControlLabel value="receita" control={<Radio />} label="Receita" />
                                            <FormControlLabel value="despesa" control={<Radio />} label="Despesa" />
                                        </RadioGroup>
                                    </MDBox>
                                    <MDBox display="flex" alignItems="center" ml={-1}>

                                    </MDBox>
                                    <MDBox mt={4} mb={1}>
                                        <MDButton disabled={isLoading} variant="gradient" color={"success"} fullWidth onClick={handleSave}>
                                            {isLoading
                                                ? <CircularProgress color="inherit" size={16} />
                                                :
                                                'Salvar'
                                            }

                                        </MDButton>
                                    </MDBox>
                                    {id && (<MDBox mt={2} mb={1}>
                                        <MDButton disabled={isLoading} variant="gradient" color={"error"} fullWidth onClick={handleDelete}>
                                            {isLoading
                                                ? <CircularProgress color="inherit" size={16} />
                                                :
                                                <Icon>delete</Icon>
                                            }

                                        </MDButton>
                                    </MDBox>)}
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            <RevenueExpenseCategoryLimitModal
                open={categoryLimitModalState.open}
                initialCategory={categoryLimitModalState.category}
                initialMaxValue={categoryLimitModalState.maxValue}
                lockCategory
                onClose={handleCategoryLimitModalClose}
                onSaved={async (savedItem) => {
                    handleCategoryLimitSaved(savedItem);
                    setCategoryLimitModalState({
                        open: false,
                        category: '',
                        maxValue: '',
                    });

                    if (shouldContinueSaveAfterCategoryLimit) {
                        setShouldContinueSaveAfterCategoryLimit(false);
                        setIsLoagding(true);
                        await persistRevenueExpense();
                    }
                }}
                openSnackBar={openSnackBar}
            />
        </StyledContainer>
    );
}


const StyledMDBox = styled(MDBox)`
  background: radial-gradient(circle at 221% 201%, #f28371 0%, #f28371 65%, transparent 0%) ,#f0ba8d !important; 
`;


const StyledInput = styled(MDBox)`
[data-input] {
    & > div > div {
        // border: 1px solid red;
        height: 44px;
        
        & > input {
            padding: 0 5px !important;
        }
    }
}
`;

const NameSuggestionContent = styled.div`
    display: flex;
    flex-direction: column;

    small {
        color: #7b809a;
    }
`;

const StyledContainer = styled.div`
    width: 100%;

    [role="dialog"]{
        margin: 50px;
    }


    // @media screen and (max-width: 767px) {
    //     padding-top: 0px !important;
    //   }
`;

const StyledDatePicker = styled(DatePicker)`
width: 100%;

&>div{
    border-radius: 8px;
    height: 43px;
    padding: 0 3px;
}

input{
    padding: 9px 10px;
    font-size: 14px;
    color: rgb(73, 80, 87);
}

label{
    font-size: 14px;
    top: -4px;
}

button{
    right: 10px
}

    ${({ $isDarkTheme }) =>
        css` 
        label{
            color: ${$isDarkTheme ? '#FFFFFFCC' : '#7B809A'};
        }    

        div {
            height: 44.5px;
            border-bottom-left-radius: 6px;
            border-bottom-right-radius: 6px;
            border-top-left-radius: 6px;
            border-top-right-radius: 6px;

            &:hover fieldset {
                border-color: ${$isDarkTheme ? 'rgba(210, 214, 218, 0.6)' : '#d2d6da'} !important;
              }

              input {
                color: ${$isDarkTheme ? '#FFFFFFCC' : '#7B809A'};
              }
          
            fieldset{
                border-top-color: ${$isDarkTheme ? 'rgba(210, 214, 218, 0.6)' : '#d2d6da'};
                border-bottom-color: ${$isDarkTheme ? 'rgba(210, 214, 218, 0.6)' : '#d2d6da'};
                border-right-color: ${$isDarkTheme ? 'rgba(210, 214, 218, 0.6)' : '#d2d6da'};
                border-left-color: ${$isDarkTheme ? 'rgba(210, 214, 218, 0.6)' : '#d2d6da'};
            }

            path{
                color: ${$isDarkTheme ? '#FFFFFFCC' : '#7B809A'};
            }
        } 
    `}
`;

export default AddNewRevenueExpenseForm;
